import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTextField from 'components/inputs/FormikTextField';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import Panel from 'components/Panel';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {settingsActions} from 'redux/settings';
import selectors from 'redux/settings/selectors';
import {FTPSettings, ftpSettingsSchema} from 'types/FTPSettings';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {useSendData} from 'features/layout/CommonPage/redux/hooks/useSendData';
import {IDiplomatObject} from 'types/DiplomatObject';
import AsyncContainer from 'components/AsyncContainer';
import {isEmpty} from 'lodash';
import Formik from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {useTranslation} from 'react-i18next';

type DialogForm = {
   ok: (values: FTPSettings, callbacks: any) => any;
   cancel: () => void;
};

const SettingsFTPForm = ({cancel}: {cancel: DialogForm['cancel']}) => {
   const {t} = useTranslation();

   const getValue = useFieldValueGetter<FTPSettings>();
   const useDefaultActiveIpAddress = getValue('useDefaultActiveIpAddress');
   const useDefaultActivePorts = getValue('useDefaultActivePorts');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   return (
      <>
         <Panel name="Active FTP Settings">
            <Grid container spacing={1}>
               <Grid item xs={5}>
                  <FormikCheckbox
                     autoFocus
                     name="useDefaultActiveIpAddress"
                     label={t('Use Default IP Address')}
                  />
               </Grid>
               <Grid item xs={7}>
                  <FormikTextField
                     name="activeIpAddress"
                     label={t('IP Address')}
                     disabled={useDefaultActiveIpAddress}
                  />
               </Grid>
               <Grid item xs={5}>
                  <FormikCheckbox name="useDefaultActivePorts" label={t('Use Default Port')} />
               </Grid>
               <Grid item xs={7}>
                  <FormikTextField
                     name="startPort"
                     label={t('Start Port Range')}
                     disabled={useDefaultActivePorts}
                  />
                  <FormikTextField
                     name="endPort"
                     label={t('End Port Range')}
                     disabled={useDefaultActivePorts}
                  />
               </Grid>
            </Grid>
         </Panel>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign="center">
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const SettingsFTPFormWrapper = ({ok, cancel}: DialogForm) => {
   const settings = useSelector(selectors.ftpSettings);
   const dispatch = useDispatch();
   React.useEffect(() => {
      dispatch(settingsActions.getFTPSettings());
   }, [dispatch]);

   return (
      <AsyncContainer loading={isEmpty(settings)}>
         <Formik
            initialValues={settings}
            validationSchema={ftpSettingsSchema}
            onSubmit={ok}
            enableReinitialize
         >
            <SettingsFTPForm cancel={cancel} />
         </Formik>
      </AsyncContainer>
   );
};

type DialogProps = {
   onClose: () => void;
};
export const SettingsFTPDialog = ({onClose}: DialogProps) => {
   const sendData = useSendData();
   return (
      <Dialog maxWidth="md" header={DialogTypes.SettingsFTPDialog.dialogName} close={onClose}>
         <SettingsFTPFormWrapper
            ok={(values, callbacks) => {
               sendData(DialogTypes.SettingsFTPDialog.id, values as IDiplomatObject, callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};
export default React.memo(SettingsFTPDialog);
